import { ArrowRight, CalendarDays, PlayCircle, User } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { Recording } from "../../api/types";
import { formatDuration } from "../../utils";
import { getBrowserHash } from "../../utils/urlHash";
import { buildSessionTitle } from "../analytics/sessionTitle";
import { sessionTiming } from "./RecentSession";
import SessionApps from "./SessionApps";
import SessionStatus from "./SessionStatus";

/** The team's most recent capture, one click away from its session page. */
export default function LatestSessionBanner({
  recording,
  person,
  revision,
}: {
  recording: Recording;
  person: string;
  revision: number;
}) {
  const { t, i18n } = useTranslation();
  const { duration, range } = sessionTiming(recording, i18n.resolvedLanguage, ", ");
  const name = buildSessionTitle(undefined, recording.id, recording);
  return (
    <section
      aria-labelledby="latest-session-title"
      className="@container rounded-xl border border-info/20 bg-info/5 p-5 @[600px]:p-6"
    >
      <div className="flex flex-col gap-5 @[1000px]:flex-row @[1000px]:items-center @[1000px]:gap-8">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <span
            aria-hidden="true"
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-info text-white"
          >
            <PlayCircle className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-info">
              {t("activity.latest_session")}
            </p>
            <div className="mt-1 flex min-w-0 items-center gap-2.5">
              <h2
                id="latest-session-title"
                className="truncate font-display text-xl font-semibold leading-snug tracking-tight"
                title={name}
              >
                {name}
              </h2>
              <SessionStatus recording={recording} />
            </div>
            <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-mid tabular-nums">
              <span className="inline-flex items-center gap-1.5">
                <User aria-hidden="true" className="h-4 w-4 text-text-dim" />
                {person}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays aria-hidden="true" className="h-4 w-4 text-text-dim" />
                {range}
              </span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
          <dl className="flex gap-7">
            <div className="@[1000px]:border-l @[1000px]:border-info/20 @[1000px]:pl-7">
              <dt className="text-xs text-text-mid">{t("activity.captured")}</dt>
              <dd className="mt-1 whitespace-nowrap font-display text-2xl font-semibold tabular-nums">
                {formatDuration(duration)}
              </dd>
            </div>
            <div className="border-l border-info/20 pl-7">
              <dt className="text-xs text-text-mid">{t("activity.apps")}</dt>
              <dd className="mt-1">
                <SessionApps recording={recording} revision={revision} alwaysVisible />
              </dd>
            </div>
          </dl>
          <Link
            to={`/recordings/${encodeURIComponent(recording.id)}/`}
            state={{ returnHash: getBrowserHash() }}
            className="group inline-flex items-center gap-2 rounded-lg bg-info px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-info-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-info"
          >
            {t("activity.open_session")}
            <ArrowRight
              aria-hidden="true"
              className="h-4 w-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
